import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useSessionStore } from '@/stores/session'

const routes: RouteRecordRaw[] = [
  // Root: redirect logic lives in HomeRedirect (fetches /me, picks an org).
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/HomeRedirect.vue'),
  },
  // Auth / public routes
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/pages/SignInPage.vue'),
    meta: { layout: 'auth' },
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: () => import('@/pages/SignUpPage.vue'),
    meta: { layout: 'auth' },
  },
  // Org-scoped dashboard routes
  {
    path: '/o/:orgSlug/today',
    name: 'Today',
    component: () => import('@/pages/TodayPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/catalog',
    name: 'Catalog',
    component: () => import('@/pages/CatalogPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/sources-sought',
    name: 'SourcesSought',
    component: () => import('@/pages/CatalogPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/catalog/:noticeId',
    name: 'OpportunityDetail',
    component: () => import('@/pages/OpportunityDetailPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/proposal-queue',
    name: 'ProposalQueue',
    component: () => import('@/pages/ProposalQueuePage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/system',
    name: 'SystemAdmin',
    component: () => import('@/pages/SystemAdminPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/contracts',
    name: 'Contracts',
    component: () => import('@/pages/ContractsPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/contracts/:cid',
    name: 'ContractDetail',
    component: () => import('@/pages/ContractDetailPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/iuid',
    name: 'IUID',
    component: () => import('@/pages/IUIDPage.vue'),
    meta: { layout: 'dashboard', requiresAuth: true },
  },
  {
    path: '/o/:orgSlug/vendors',
    name: 'Vendors',
    component: () => import('@/pages/VendorsPage.vue'),
    meta: { layout: 'dashboard', requiresAuth: true },
  },
  {
    path: '/o/:orgSlug/tasks',
    name: 'Tasks',
    component: () => import('@/pages/TasksPage.vue'),
    meta: { layout: 'dashboard', requiresAuth: true },
  },
  {
    path: '/o/:orgSlug/board',
    name: 'Board',
    component: () => import('@/pages/BoardPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/pursuits/:id',
    name: 'PursuitDetail',
    component: () => import('@/pages/PursuitDetailPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/proposals/:pid',
    name: 'ProposalEditor',
    component: () => import('@/pages/ProposalEditorPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/calendar',
    name: 'Calendar',
    component: () => import('@/pages/CalendarPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  {
    path: '/o/:orgSlug/settings',
    name: 'Settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: { requiresAuth: true, layout: 'dashboard' },
  },
  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFound.vue'),
  },
]

const router = createRouter({
  // Browser back/forward restores the scroll position it left at.
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
  history: createWebHistory(),
  routes,
})

// Keep the session store's active org in sync with the URL so deep links
// (/o/<org>/...) send the right X-Org-ID from the first request. Auth itself
// is handled by Clerk — DashboardLayout redirects to /sign-in when signed out.
router.beforeEach((to) => {
  const orgSlug = to.params.orgSlug
  if (typeof orgSlug === 'string' && orgSlug) {
    useSessionStore().setActiveOrg(orgSlug)
  }
  return true
})

export default router
